import { useCallback, useEffect, useMemo } from 'react'
import { useExportCSVMutation } from 'src/app/appApiQueries'
import { Emoji, NormalizedParticipant, Participant } from 'src/app/types'
import { addErrorSDKToast } from 'src/utils/helpers'
import { normalizeParticipantData } from 'src/utils/normalizeCSV'

type UseCreateCsvOptions =
  | {
      participantArr: Participant[]
      normalizedParticipants?: never
      allBadges?: never
      name?: string
    }
  | {
      participantArr?: never
      normalizedParticipants: NormalizedParticipant[]
      allBadges: Emoji[]
      name?: string
    }

export const useCreateCsv = ({
  participantArr,
  normalizedParticipants,
  allBadges,
  name,
}: UseCreateCsvOptions) => {
  const [exportCSV, { isLoading, data: csvUrl, error }] = useExportCSVMutation()

  // if normalizedParticipants && allBadges are provided, we dont want to normalize it again because these are processed data from Leaderboard
  const data = useMemo(
    () =>
      normalizedParticipants && allBadges
        ? { normalizedParticipants, allBadges }
        : normalizeParticipantData(participantArr!),
    [normalizedParticipants, allBadges, participantArr]
  )

  useEffect(() => {
    const downloadCSVFile = async () => {
      if (!error && csvUrl) {
        try {
          await window.zoomSdk.openUrl({ url: csvUrl })
        } catch (e) {
          addErrorSDKToast(e)
        }
      }
    }

    downloadCSVFile()
  }, [error, csvUrl])

  const onExportToCSV = useCallback(() => {
    exportCSV({
      csv: {
        sessionName: name || 'default',
        participants: data.normalizedParticipants,
        headers: data.allBadges,
      },
    })
  }, [exportCSV, data, name])

  return { onExportToCSV, isLoading }
}
